import { faVideoSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { nanoid } from "@reduxjs/toolkit";
import { MediaSourceVideoFile } from "cloud-core/media/MediaSource";
import { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import OverlaySpinner from "../../../components/OverlaySpinner";
import useAsyncCallback from "../../../hooks/useAsyncCallback";
import useMaxContainerDimensions from "../../../hooks/useMaxContainerDimensions";
import useMediaSources from "../../../hooks/useMediaSources";
import { useView } from "../../../hooks/useView";
import { calculateFramePeriod, getDisplayVideoAtTimestamp } from "../../../models/media/MediaSource";
import { getVideoProcessingTaskStatus } from "../../../models/tasks/Task";
import { Entity } from "../../../models/viz/Entity";
import { ApplicationState } from "../../../store";
import * as AnalyticsStore from "../../../store/analytics/Analytics";
import { useGetStoredFilesForMediaQuery, useGetTasksQuery } from "../../../store/api/kinesense";
import { MediaSourceWithExtras } from "../../../store/media/MediaItems";
import { Notifications } from "../../../utilities/Notifications/Notifications";
import { isInRange } from "../../../utilities/helpers";
import { useVideoControls } from "./VideoControls";
import VideoOverlay from "./VideoOverlay";
import "./VideoPlayer.scss";
import { FragmentsMap } from "./useLoadFragments";

/** Helper function for determining whether a given timestamp (in milliseconds) is a valid cursor position */
function isTimestampValid(timestamp: number, videoDuration: number) {
    const notificationId = "invalidTimestamp" as const;

    if (isNaN(timestamp)) {
        Notifications.notify(
            "Invalid timestamp",
            "The specified timestamp is not a valid number",
            "important",
            notificationId,
        );
    } else if (!isInRange(timestamp, [0, videoDuration + 1])) {
        Notifications.notify(
            "Timestamp out of range",
            `The specified timestamp is outside the duration of the current video. Valid timestamps for this video must be within the range 0-${videoDuration}`,
            "important",
            notificationId,
        );
    } else {
        return true;
    }

    return false;
}

/** Handle re-fetching of an active media source which is done being processed */
function useHandleVideoProcessing(mediaId: string, refetchActiveMedia: () => Promise<MediaSourceWithExtras>) {
    const projectId = useSelector((state: ApplicationState) => state.general?.activeProjectId);
    const { data: tasks } = useGetTasksQuery({ projectId }, { skip: !projectId });

    const matchingTaskIndex = useMemo(() => {
        return tasks?.findIndex((t) => t.references.mediaId === mediaId);
    }, [tasks]);
    const matchingTask = matchingTaskIndex === undefined ? undefined : tasks[matchingTaskIndex];

    const isProcessing = getVideoProcessingTaskStatus(matchingTask)?.isProcessing ?? false;
    const prevIsProcessing = useRef(false);

    useAsyncCallback(async () => {
        // Re-fetch active media source if it has just finished transcoding
        if (!isProcessing && prevIsProcessing.current) {
            // HACK: retries are required because the `link` task step finishing does not mean that
            // the media source will immediately have access to the `initialDisplay` file, only that
            // the link job has been started on the backend.
            // TODO: remove retries when the backend is updated to only finish the task step when the
            // corresponding job finishes
            let retries = 3;
            while (retries > 0) {
                try {
                    const media = await refetchActiveMedia();
                    if (media?.files?.initialDisplay?.fileId) {
                        break;
                    }
                } finally {
                    retries -= 1;
                    await new Promise((resolve) => setTimeout(resolve, 750));
                }
            }
        }
        prevIsProcessing.current = isProcessing;
    }, [isProcessing]);

    return isProcessing;
}

export interface VideoPlayerProps {
    videoType: string;
    viewId: string;
    mediaId: string;
    videoRef: React.MutableRefObject<HTMLVideoElement>;
    canvasRef: React.MutableRefObject<HTMLCanvasElement>;
    entitiesInView: Entity[];
    fragmentsMap: FragmentsMap;
    timestampParam?: string | null;
}

function VideoPlayer(props: VideoPlayerProps) {
    const dispatch = useDispatch();

    const { view } = useView(props.viewId);
    const {
        activeMediaSource,
        refetchActiveMedia,
        hasLoadedActiveMediaInitial: hasLoadedActiveMediaSource,
    } = useMediaSources(props.viewId, props.mediaId);

    const refetchActiveMediaUnwrap = () => refetchActiveMedia().unwrap();
    const isProcessing = useHandleVideoProcessing(props.mediaId, refetchActiveMediaUnwrap);

    const allowVideoStretching = view?.allowVideoStretching ?? true;
    const scale = allowVideoStretching ? 2 : 1;

    const [activeVideoFile, setActiveVideoFile] = useState<MediaSourceVideoFile | undefined>(
        activeMediaSource?.displayFiles[0],
    );
    const {
        data: storedFiles,
        isError,
        isLoading,
        isUninitialized,
        refetch: refetchStoredFile,
    } = useGetStoredFilesForMediaQuery(
        { projectId: activeMediaSource?.projectId, mediaId: activeMediaSource?.mediaId },
        { skip: activeMediaSource?.mediaId === undefined },
    );

    const hasLoadedStoredFile = !isLoading && !isUninitialized;
    const hasNoStoredFile = isError || (hasLoadedActiveMediaSource && activeMediaSource?.displayFiles.length === 0);
    const [storedFileExpiresAt, setStoredFileExpiresAt] = useState(undefined);

    const [hasLoadedVideo, setHasLoadedVideo] = useState(false);
    const playerId = useRef("vp-" + nanoid(8));

    const isLoadingMedia = !hasLoadedActiveMediaSource || !hasLoadedVideo;

    // For calculating video dimensions
    const containerRef = useRef<HTMLDivElement>(null);
    const videoCanvasRef = useRef<HTMLCanvasElement>(null);
    const [containerWidth, containerHeight] = useMaxContainerDimensions(containerRef);
    const srcHeight = props.videoRef.current?.videoHeight;
    const srcWidth = props.videoRef.current?.videoWidth;

    const [useFirstVideoElement, setUseFirstVideoElement] = useState(true);
    const secondaryVideoRef = useRef<HTMLVideoElement>(null);
    const [nextVideo, setNextVideo] = useState<MediaSourceVideoFile | undefined>(undefined);

    // Controlled video dimensions
    const videoDimensions: [number, number] = useMemo(() => {
        if (!srcWidth || !srcHeight || !containerWidth || !containerHeight) {
            return [0, 0];
        }

        const aspectRatio = srcWidth / srcHeight;

        const maxWidth = Math.min(srcWidth * scale, containerWidth);
        const maxHeight = Math.min(srcHeight * scale, containerHeight);

        // By default, use max height with a matching width
        let videoHeight = maxHeight;
        let videoWidth = aspectRatio * videoHeight;

        // If the width is too big, reduce the dimensions accordingly
        if (videoWidth > maxWidth) {
            videoWidth = maxWidth;
            videoHeight = videoWidth / aspectRatio;
        }

        return [videoWidth, videoHeight];
    }, [containerWidth, containerHeight, srcWidth, srcHeight, allowVideoStretching]);

    const [videoElementDimensions, setVideoElementDimensions] = useState<[number, number]>([0, 0]);

    // Sync view states with video element's own state.
    // Needed because picture-in-picture mode has video controls
    const { play, pause, mute, unmute, videoPlayerOnTimeUpdate } = useVideoControls(
        props.viewId,
        props.mediaId,
        props.videoRef,
        videoCanvasRef,
        playerId,
        view?.filteredEntities,
        props.entitiesInView,
    );

    function onSeeked() {
        videoPlayerOnTimeUpdate();
    }

    function onPause() {
        // check if it's the final video in the media source
        const sortedFiles = activeMediaSource?.displayFiles?.toSorted((a, b) => a.startsAt - b.startsAt);
        const lastVideoFile = sortedFiles?.[sortedFiles.length - 1];
        const currentTime = (props.videoRef.current?.currentTime ?? 0) * 1000;
        const currentFileId = props.videoRef.current?.getAttribute("data-file-id");

        if (lastVideoFile && lastVideoFile.fileId === currentFileId && currentTime >= lastVideoFile.duration) {
            pause();
        }
    }

    function onVideoEnded() {
        const activeVideoDuration = Number(props.videoRef.current.getAttribute("data-video-duration")) || 0;
        const frameMediaTime = props.videoRef.current.currentTime * 1000;
        const framePeriod = calculateFramePeriod(activeMediaSource?.files?.initialDisplay);

        if (frameMediaTime + framePeriod >= activeVideoDuration) {
            const activeVideoIndex = activeMediaSource?.displayFiles.findIndex(
                (file) => file.fileId === activeVideoFile.fileId,
            );

            if (activeVideoIndex + 1 < activeMediaSource?.displayFiles.length) {
                const nextActiveVideo = activeMediaSource?.displayFiles[activeVideoIndex + 1];
                const loadedNextVideoId = secondaryVideoRef.current?.getAttribute("data-file-id");

                if (nextActiveVideo.fileId == loadedNextVideoId) {
                    setUseFirstVideoElement(!useFirstVideoElement);
                    setActiveVideoFile(nextActiveVideo);
                } else {
                    props.videoRef.current.autoplay = true;
                    setActiveVideo(nextActiveVideo);
                }
            }
        } else {
            pause();
        }
    }

    function setActiveVideo(videoFile: MediaSourceVideoFile) {
        setVideoAttributes(props.videoRef, videoFile);
        setActiveVideoFile(videoFile);

        const expiry = storedFiles?.get(videoFile.fileId)?.accessUrl?.expiresAt;
        if (expiry) {
            setStoredFileExpiresAt(expiry);
        } else {
            setStoredFileExpiresAt(undefined);
        }
    }

    function setVideoAttributes(ref: React.MutableRefObject<HTMLVideoElement>, videoFile: MediaSourceVideoFile) {
        if (!ref.current) {
            return;
        }

        ref.current.setAttribute("data-video-start", `${videoFile.startsAt}`);
        ref.current.setAttribute("data-video-duration", `${videoFile.duration}`);
        ref.current.setAttribute("data-file-id", videoFile.fileId);
        ref.current.setAttribute("data-finished-load", false.toString());
    }

    function setVideoSource(ref: React.MutableRefObject<HTMLVideoElement>, videoFile: MediaSourceVideoFile) {
        const storedFileUrl = storedFiles?.get(videoFile.fileId)?.accessUrl?.url;

        if (storedFileUrl) {
            setVideoAttributes(ref, videoFile);
            ref.current.src = storedFileUrl;
            ref.current.load();
        } else {
            console.warn("VideoPlayer: No stored file URL found for video file", videoFile.fileId);
        }
    }

    function loadInitialVideo() {
        if (props.videoRef.current?.src !== "") {
            return;
        }

        const initialFileId = activeMediaSource?.displayFiles?.[0]?.fileId;
        const initialStoredFile = storedFiles?.get(initialFileId);
        const initialStoredFileUrl = initialStoredFile?.accessUrl?.url;

        props.videoRef.current.src = initialStoredFileUrl;
    }

    function loadVideo() {
        let videoFile = activeVideoFile;

        if (videoFile === undefined) {
            const fileId = activeMediaSource?.displayFiles?.[0]?.fileId;
            videoFile = activeMediaSource.displayFiles?.find((file) => file.fileId === fileId);
            setActiveVideo(videoFile);
        }

        if (view?.cursor === undefined) {
            loadInitialVideo();
            let newCursorTime = activeMediaSource.startsAt;

            if (hasLoadedVideo) {
                setHasLoadedVideo(false);
            }
            // Set video start time to the provided timestamp, if this is the first video load and if it is valid
            else if (props.timestampParam) {
                const timestamp = parseInt(props.timestampParam);
                if (isTimestampValid(timestamp, activeMediaSource.duration)) {
                    newCursorTime += timestamp;
                }
            }

            dispatch(
                AnalyticsStore.actionCreators.setPrimaryCursorPosition(
                    props.viewId,
                    newCursorTime,
                    "playback",
                    playerId.current,
                ),
            );

            if (props.videoRef.current != null) {
                props.videoRef.current.load();
                props.videoRef.current.currentTime = (newCursorTime - activeMediaSource.startsAt) / 1000;
            }
        } else {
            if (props.videoRef.current?.src === "") {
                loadInitialVideo();
            }

            const newVideoTime = (view?.cursor - activeMediaSource.startsAt - videoFile.startsAt) / 1000;
            props.videoRef.current.currentTime = newVideoTime;
            props.videoRef.current.setAttribute("data-init-time", `${newVideoTime}`);
        }
    }

    useEffect(() => {
        if (videoDimensions[0] > 0 && videoDimensions[1] > 0) {
            setVideoElementDimensions(videoDimensions);
        }
    }, [videoDimensions]);

    useEffect(() => {
        if (props.videoRef.current === null) {
            return;
        }

        if (view?.mediaStates?.play == "playing") {
            props.videoRef.current.play();
        }
    }, [useFirstVideoElement]);

    useEffect(() => {
        if (!props.videoRef.current || props.videoRef.current.src === "") {
            return;
        }

        let frameCallbackHandle: number;
        function step(now: DOMHighResTimeStamp, metadata: { mediaTime: number; presentedFrames: number }) {
            videoPlayerOnTimeUpdate();

            frameCallbackHandle = props.videoRef.current.requestVideoFrameCallback(step);
        }

        step(0, { mediaTime: 0, presentedFrames: 0 });
        props.videoRef.current.setAttribute("data-finished-load", "true");

        return () => {
            if (frameCallbackHandle !== undefined) {
                props.videoRef?.current?.cancelVideoFrameCallback(frameCallbackHandle);
            }
        };
    }, [props.videoRef?.current?.src]);

    useEffect(() => {
        if (activeMediaSource === undefined) {
            return;
        }

        if (props.videoRef?.current === null || view === undefined) {
            return;
        }

        // For syncing analytics media's muted state with video element's muted state
        function handleVolumeChange(e: Event) {
            (e.currentTarget as HTMLVideoElement).muted ? mute() : unmute();
        }

        props.videoRef.current?.addEventListener("play", play);
        props.videoRef.current?.addEventListener("pause", onPause);
        props.videoRef.current?.addEventListener("ended", onVideoEnded);
        props.videoRef.current?.addEventListener("volumechange", handleVolumeChange);
        props.videoRef.current?.addEventListener("seeked", onSeeked);

        return () => {
            props.videoRef.current?.removeEventListener("play", play);
            props.videoRef.current?.removeEventListener("pause", onPause);
            props.videoRef.current?.removeEventListener("ended", onVideoEnded);
            props.videoRef.current?.removeEventListener("volumechange", handleVolumeChange);
            props.videoRef.current?.removeEventListener("seeked", onSeeked);
        };
    }, [props.videoRef?.current, view === undefined, activeMediaSource]);

    useEffect(() => {
        if (storedFiles === undefined || nextVideo === undefined) {
            return;
        }

        const nextStoredFile = storedFiles.get(nextVideo?.fileId);

        if (nextStoredFile != undefined && secondaryVideoRef.current != null) {
            setVideoSource(secondaryVideoRef, nextVideo);
        }
    }, [nextVideo]);

    useEffect(() => {
        if (view === undefined || activeVideoFile === undefined || activeMediaSource === undefined) {
            return;
        }

        const startsAt = activeVideoFile?.startsAt + activeMediaSource.startsAt;
        // check if the new time is near the end of the video
        const nearEndThreshold = 10000; // milliseconds
        const endsAt = startsAt + activeVideoFile?.duration;
        if (view.cursor + nearEndThreshold > endsAt && view.cursor < endsAt) {
            const newNextVideo = getDisplayVideoAtTimestamp(activeMediaSource, view.cursor + nearEndThreshold);

            if (newNextVideo?.fileId != activeVideoFile?.fileId) {
                setNextVideo(newNextVideo);
            }
        }

        if (view.meta.cursorUpdateOrigin !== "playback") {
            const isOutsideOfCurrentVideo =
                view.cursor < startsAt || view.cursor > startsAt + activeVideoFile?.duration;

            let updatedActiveVideoFile = activeVideoFile;
            if (isOutsideOfCurrentVideo) {
                updatedActiveVideoFile = getDisplayVideoAtTimestamp(activeMediaSource, view.cursor);

                if (!updatedActiveVideoFile) {
                    console.warn("VideoPlayer: No video found at cursor", view.cursor);
                    return;
                }

                setVideoSource(props.videoRef, updatedActiveVideoFile);
                setActiveVideo(updatedActiveVideoFile);

                if (view.mediaStates.play === "playing") {
                    props.videoRef.current.play();
                }
            } else {
                const newTime = (view.cursor - activeMediaSource.startsAt - updatedActiveVideoFile.startsAt) / 1000;

                if (Number.isFinite(newTime)) {
                    props.videoRef.current.currentTime = newTime;
                } else {
                    console.warn(
                        "VideoPlayer: Invalid currentTime",
                        newTime,
                        view.cursor,
                        activeMediaSource.startsAt,
                        updatedActiveVideoFile.startsAt,
                    );
                }
            }
        }
    }, [view?.cursor]);

    useEffect(() => {
        if (activeVideoFile !== undefined) {
            loadVideo();
        }
    }, [activeVideoFile]);

    useEffect(() => {
        if (!hasLoadedStoredFile || props.viewId === undefined || props.videoRef.current === null) {
            return;
        }

        const videoHasLoadedCallback = () => {
            setHasLoadedVideo(true);
        };
        props.videoRef.current.addEventListener("loadeddata", videoHasLoadedCallback, true);

        loadVideo();

        return () => {
            props.videoRef?.current?.removeEventListener("loadeddata", videoHasLoadedCallback, true);
        };
    }, [hasLoadedStoredFile, props.viewId]);

    useEffect(() => {
        if (storedFileExpiresAt === undefined || props.viewId === undefined) {
            return;
        }

        const millisecondsToExpiration = storedFileExpiresAt - Date.now();

        // If expiration time has already passed, refetch stored file
        if (millisecondsToExpiration <= 0) {
            refetchStoredFile();
            return;
        }

        // Handle future expiry by refetching stored file before expiration time
        const timeout = setTimeout(refetchStoredFile, millisecondsToExpiration - 10000);

        return () => {
            clearTimeout(timeout);
        };
    }, [storedFileExpiresAt]);

    return (
        <div className="video-player d-flex align-items-center justify-content-center" ref={containerRef}>
            <OverlaySpinner isLoading={(isLoadingMedia || !hasLoadedStoredFile) && !hasNoStoredFile && !isProcessing} />

            <div className="video-container">
                {isProcessing ? (
                    <div role="status" className="gap-3 p-3 rounded h6 no-playable-media d-flex align-items-center">
                        <Spinner size="sm" />
                        <p className="m-0">The media source is being processed for display</p>
                    </div>
                ) : hasNoStoredFile ? (
                    <div role="status" className="gap-3 p-3 rounded h6 no-playable-media d-flex align-items-center">
                        <FontAwesomeIcon icon={faVideoSlash} />
                        <p className="m-0">No playable media found for this source</p>
                    </div>
                ) : (
                    <>
                        <canvas
                            className="video-canvas"
                            width={videoElementDimensions[0]}
                            height={videoElementDimensions[1]}
                            ref={videoCanvasRef}
                            style={{ display: "none" }}
                        />

                        <div style={{ backgroundColor: "black" }}>
                            <video
                                width={videoElementDimensions[0]}
                                height={videoElementDimensions[1]}
                                className="video-canvas"
                                style={{ display: useFirstVideoElement ? "block" : "none" }}
                                role="region"
                                aria-label="video player"
                                ref={useFirstVideoElement ? props.videoRef : secondaryVideoRef}
                                disablePictureInPicture
                                data-vid="one"
                            />
                            <video
                                width={videoElementDimensions[0]}
                                height={videoElementDimensions[1]}
                                className="video-canvas"
                                style={{ display: useFirstVideoElement ? "none" : "block" }}
                                role="region"
                                aria-label="video player"
                                ref={useFirstVideoElement ? secondaryVideoRef : props.videoRef}
                                disablePictureInPicture
                                data-vid="two"
                            />
                        </div>

                        {!isLoadingMedia && (
                            <VideoOverlay
                                canvasRef={props.canvasRef}
                                videoRef={props.videoRef}
                                viewId={props.viewId}
                                media={activeMediaSource}
                                videoPlayerOnTimeUpdate={videoPlayerOnTimeUpdate}
                                fragmentsMap={props.fragmentsMap}
                                entitiesInView={props.entitiesInView}
                                maxDimensions={videoDimensions}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default VideoPlayer;
